import React from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, X, ArrowLeft } from 'lucide-react';

export default function ReportSettings({
  onBack, settings, setSettings, participants, setParticipants,
  teamMembers, onSave, onAddParticipant, onRemoveParticipant
}) {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Reporting Settings</h1>
          </div>
          <Button onClick={onSave} className="bg-cyan-600 hover:bg-cyan-700" size="sm" data-testid="save-settings-btn"><CheckCircle className="h-4 w-4 mr-1" /> Save Settings</Button>
        </div>

        {settings && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">General Settings</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Frequency</Label>
                <Select value={settings.frequency} onValueChange={v => setSettings({...settings, frequency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Deadline Time</Label><Input value={settings.deadline_time} onChange={e => setSettings({...settings, deadline_time: e.target.value})} /></div>
              <div><Label className="text-xs">Grace Period (days)</Label><Input type="number" value={settings.grace_period_days} onChange={e => setSettings({...settings, grace_period_days: parseInt(e.target.value) || 0})} /></div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={settings.auto_ai_on_submit} onChange={e => setSettings({...settings, auto_ai_on_submit: e.target.checked})} />
                <Label className="text-xs">Auto AI analysis on submit</Label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.auto_combine_on_all_submitted} onChange={e => setSettings({...settings, auto_combine_on_all_submitted: e.target.checked})} />
                <Label className="text-xs">Auto-combine when all submitted</Label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.file_uploads_mandatory} onChange={e => setSettings({...settings, file_uploads_mandatory: e.target.checked})} />
                <Label className="text-xs">File uploads mandatory</Label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Participants */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Reporting Participants</CardTitle>
              <Select onValueChange={onAddParticipant}>
                <SelectTrigger className="w-56 h-8 text-xs"><SelectValue placeholder="Add participant..." /></SelectTrigger>
                <SelectContent>
                  {teamMembers.filter(m => !participants.find(p => p.user_id === m.id)).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.first_name || ''} {m.surname || m.last_name || ''} ({m.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {participants.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No participants configured. Add team members above.</p>
            ) : (
              <div className="space-y-2">
                {participants.map(p => (
                  <div key={p.user_id} className="flex items-center gap-3 border rounded-lg px-3 py-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{p.user_name}</p>
                      <p className="text-xs text-slate-500">{p.company_name} — {p.email}</p>
                    </div>
                    <Select value={p.participant_type} onValueChange={v => setParticipants(participants.map(x => x.user_id === p.user_id ? {...x, participant_type: v} : x))}>
                      <SelectTrigger className="w-36 h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultant">Consultant</SelectItem>
                        <SelectItem value="subcontractor">Subcontractor</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => onRemoveParticipant(p.user_id)}><X className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
